import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Stage, Layer, Line, Image as KonvaImage, Rect } from 'react-konva';
import { Upload, Loader2, Hand, AlertCircle, X, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useImage from 'use-image';
import WebcamPalmReader from './WebcamPalmReader';

interface ReadingResult {
  reading: string;
  callToAction: string;
}

interface PalmLine {
  name: string;
  color: string;
  description: string;
}

const PALM_LINES: PalmLine[] = [
  {
    name: 'Life Line',
    color: '#FF6B6B',
    description: 'Shows vitality, life energy, and major life changes'
  },
  {
    name: 'Heart Line',
    color: '#4ECDC4',
    description: 'Reflects emotional life and relationships'
  },
  {
    name: 'Head Line',
    color: '#45B7D1',
    description: 'Indicates intellectual capabilities and communication style'
  },
  {
    name: 'Fate Line',
    color: '#96CEB4',
    description: 'Represents career path and life direction'
  }
];

const PalmImage = ({ src, width, height }: { src: string; width: number; height: number }) => {
  const [palmImage] = useImage(src);
  return palmImage ? <KonvaImage image={palmImage} width={width} height={height} /> : null;
};

export default function PalmReading() {
  const navigate = useNavigate();
  const [image, setImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReadingResult | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [scanPosition, setScanPosition] = useState(0);
  const scanAnimationRef = useRef<number>();
  const stageRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [useWebcam, setUseWebcam] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result as string);
        setResult(null); // Reset result when new image is uploaded
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024 // 5MB
  });

  useEffect(() => {
    if (image) {
      const img = new Image();
      img.src = image;
      img.onload = () => {
        // Get container width
        const containerWidth = containerRef.current?.clientWidth || 500;
        const maxHeight = 400; // Maximum height for the image
        
        // Calculate aspect ratio
        const aspectRatio = img.width / img.height;
        
        // Determine dimensions based on constraints
        let width = Math.min(containerWidth - 40, img.width); // Subtract padding
        let height = width / aspectRatio;
        
        // If height exceeds max height, recalculate
        if (height > maxHeight) {
          height = maxHeight;
          width = height * aspectRatio;
        }
        
        setImageSize({
          width,
          height
        });
      };
    }
  }, [image]);

  // Recalculate image size on window resize
  useEffect(() => {
    const handleResize = () => {
      if (image) {
        const img = new Image();
        img.src = image;
        img.onload = () => {
          const containerWidth = containerRef.current?.clientWidth || 500;
          const maxHeight = 400;
          const aspectRatio = img.width / img.height;
          
          let width = Math.min(containerWidth - 40, img.width);
          let height = width / aspectRatio;
          
          if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
          }
          
          setImageSize({
            width,
            height
          });
        };
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [image]);

  // Cleanup animation frame on unmount or when analysis is reset
  useEffect(() => {
    return () => {
      if (scanAnimationRef.current) {
        cancelAnimationFrame(scanAnimationRef.current);
        scanAnimationRef.current = undefined;
      }
    };
  }, []);

  const animateScan = useCallback(() => {
    let startTime: number | null = null;
    const duration = 1500; // 1.5 seconds per scan
    const totalScans = 2; // Number of up-and-down scans

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = (elapsed % duration) / duration;
      const currentScan = Math.floor(elapsed / duration);

      if (currentScan < totalScans * 2) { // Multiply by 2 for up and down
        // Calculate position based on whether we're on an up or down scan
        const isUpward = currentScan % 2 === 1;
        const position = isUpward 
          ? (1 - progress) * imageSize.height 
          : progress * imageSize.height;
        
        setScanPosition(position);
        scanAnimationRef.current = requestAnimationFrame(animate);
      } else {
        setScanPosition(0);
        setAnalyzing(false);
        // Now show the results after the animation is complete
        handleShowResults();
      }
    };

    scanAnimationRef.current = requestAnimationFrame(animate);
  }, [imageSize.height]);

  const handleShowResults = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-palm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ image }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze palm');
      }

      const data = await response.json();
      setResult(data);
      setLoading(false);
    } catch (err) {
      setError('Unable to analyze palm at this time. Please try again later.');
      setLoading(false);
    }
  };

  const handleAnalysis = () => {
    if (!image) return;

    setLoading(true);
    setError(null);
    setAnalyzing(true);
    animateScan();
  };

  const resetAnalysis = useCallback(() => {
    // Cancel any ongoing animation
    if (scanAnimationRef.current) {
      cancelAnimationFrame(scanAnimationRef.current);
      scanAnimationRef.current = undefined;
    }
    
    setImage(null);
    setResult(null);
    setError(null);
    setScanPosition(0);
    setAnalyzing(false);
    setUseWebcam(false);
  }, []);

  const handleWebcamCapture = (imageData: string) => {
    setImage(imageData);
    setUseWebcam(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600 py-16">
      <div className="max-w-4xl mx-auto px-4" ref={containerRef}>
        <div className="text-center text-white mb-12">
          <h1 className="text-4xl font-bold mb-4">AI Palm Reading</h1>
          <p className="text-xl text-purple-200">
            Discover insights about your life path through the ancient art of palmistry
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8">
          {!result ? (
            <>
              <div className="text-center mb-8">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Hand className="w-8 h-8 text-purple-600" />
                </div>
                <h2 className="text-2xl font-semibold mb-2">Upload Your Palm Photo</h2>
                <p className="text-gray-600">
                  Take a clear photo of your dominant hand's palm in good lighting
                </p>
                <div className="flex justify-center mt-4 space-x-4">
                  <button
                    onClick={() => setUseWebcam(false)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      !useWebcam 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    <Upload className="w-5 h-5 mr-2 inline" />
                    Upload Photo
                  </button>
                  <button
                    onClick={() => setUseWebcam(true)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      useWebcam 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    <Camera className="w-5 h-5 mr-2 inline" />
                    Use Camera
                  </button>
                </div>
              </div>

              {useWebcam ? (
                <WebcamPalmReader 
                  onCapture={handleWebcamCapture}
                  onCancel={() => setUseWebcam(false)}
                />
              ) : (
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragActive
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300 hover:border-purple-500 hover:bg-purple-50'
                  }`}
                >
                  <input {...getInputProps()} />
                  {image && imageSize.width > 0 ? (
                    <div className="relative flex justify-center">
                      <div className="relative" style={{ maxWidth: '100%', maxHeight: '400px', overflow: 'hidden' }}>
                        <Stage
                          ref={stageRef}
                          width={imageSize.width}
                          height={imageSize.height}
                          className="mx-auto"
                        >
                          <Layer>
                            <PalmImage
                              src={image}
                              width={imageSize.width}
                              height={imageSize.height}
                            />
                            {analyzing && (
                              <Rect
                                x={0}
                                y={scanPosition - 8}
                                width={imageSize.width}
                                height={16}
                                fill="red"
                                opacity={0.6}
                              />
                            )}
                          </Layer>
                        </Stage>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            resetAnalysis();
                          }}
                          className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-lg hover:bg-gray-100"
                        >
                          <X className="w-5 h-5 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 text-purple-500 mx-auto mb-4" />
                      <p className="text-gray-600">
                        {isDragActive
                          ? "Drop your palm photo here"
                          : "Drag and drop your palm photo here, or click to select"}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg flex items-center text-red-700">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  <p>{error}</p>
                </div>
              )}

              {image && !useWebcam && (
                <div className="mt-6 text-center">
                  <button
                    onClick={handleAnalysis}
                    disabled={loading || analyzing}
                    className="bg-purple-600 text-white px-8 py-3 rounded-full hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading || analyzing ? (
                      <span className="flex items-center">
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {analyzing ? 'Analyzing Palm...' : 'Processing...'}
                      </span>
                    ) : (
                      'Analyze My Palm'
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="max-w-2xl mx-auto">
              <h3 className="text-2xl font-semibold mb-6">Your Palm Reading</h3>
              
              {/* Palm Lines Legend */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {PALM_LINES.map((line, index) => (
                  <div
                    key={index}
                    className="flex items-center p-3 rounded-lg bg-gray-50"
                  >
                    <div
                      className="w-4 h-4 rounded-full mr-3"
                      style={{ backgroundColor: line.color }}
                    />
                    <div>
                      <h4 className="font-medium">{line.name}</h4>
                      <p className="text-sm text-gray-600">{line.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="prose max-w-none mb-8">
                <p className="text-gray-700 leading-relaxed">{result.reading}</p>
              </div>
              
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-6 mb-8">
                <p className="text-purple-800">{result.callToAction}</p>
              </div>
              
              <div className="flex justify-between items-center">
                <button
                  onClick={resetAnalysis}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Try Another Reading
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="bg-purple-600 text-white px-6 py-2 rounded-full hover:bg-purple-700 transition-colors"
                >
                  Connect with a Psychic
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-white">
          <div className="text-center">
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-2">Professional Analysis</h3>
              <p className="text-purple-200">
                Get deeper insights from our experienced palm readers
              </p>
            </div>
          </div>
          <div className="text-center">
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-2">Future Predictions</h3>
              <p className="text-purple-200">
                Understand what your palm lines reveal about your destiny
              </p>
            </div>
          </div>
          <div className="text-center">
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-2">Life Path Guidance</h3>
              <p className="text-purple-200">
                Get personalized advice for your life's journey
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}